import { NavLink } from 'react-router-dom'
import { X } from 'lucide-react'
import type { siteContent } from '../data/siteContent'
import { useTranslation } from '../context/LanguageContext'

interface MobileNavDrawerProps {
  brand: typeof siteContent.brand
  navItems: string[]
  onClose: () => void
}

export function MobileNavDrawer({ brand, navItems, onClose }: MobileNavDrawerProps) {
  const { t } = useTranslation()
  const tagline = t(
    'mobile.tagline',
    'Custom pet portraits in ink and oil — hand-painted from your photos, shipped worldwide.',
  )

  return (
    <div
      id="mobile-navigation"
      role="dialog"
      aria-modal="true"
      aria-label="Mobile navigation"
      className="fixed inset-0 z-50 flex lg:hidden bg-black/40 backdrop-blur-sm"
    >
      <div
        className="relative ml-0 h-full w-full max-w-sm bg-gradient-to-b from-[#fdf8f3] via-secondary to-[#f3f3f5] text-foreground shadow-2xl shadow-background/40 border-r border-border pt-[calc(1.25rem+env(safe-area-inset-top,0px))] pb-[calc(2.25rem+env(safe-area-inset-bottom,0px))] px-6 flex flex-col"
      >
        <div className="flex items-start justify-between border-b border-border/70 pb-4 mb-4">
          <div className="max-w-[26ch]">
            <p className="font-serif text-[1.6rem] leading-snug font-semibold tracking-tight text-foreground">
              {t('brand.name', brand.name)}
            </p>
            {brand.heroSubtitle && (
              <p className="mt-2 text-sm text-muted-foreground leading-snug">
                {t('brand.heroSubtitle', brand.heroSubtitle)}
              </p>
            )}
              <div className="h-4 md:h-5" />
          </div>
          <button
            type="button"
            aria-label="Close menu"
            className="inline-flex h-9 w-9 mr-1 items-center justify-center rounded-full border border-border/70 bg-card/70 text-foreground/70 shadow-sm transition-colors hover:text-primary hover:border-primary/70 hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex flex-col gap-1 overflow-y-auto">
          {navItems.map((item) => {
            const path =
              item === 'Home'
                ? '/'
                : item === 'Artworks'
                  ? '/artworks'
                  : `/${item.toLowerCase()}`

            return (
              <NavLink
                key={item}
                to={path}
                className={({ isActive }) =>
                  `group flex items-center justify-between rounded-xl px-3 py-3 text-base font-medium tracking-wide transition-colors ${
                    isActive
                      ? 'bg-primary/8 text-primary border border-primary/40 shadow-sm'
                      : 'text-foreground/80 border border-transparent hover:border-border/80 hover:bg-muted/70 hover:text-primary'
                  }`
                }
                onClick={onClose}
              >
                <span>{t(`nav.${item.toLowerCase()}`, item)}</span>
                <span className="h-1.5 w-1.5 rounded-full bg-primary/70 opacity-0 group-[.active]:opacity-100 group-hover:opacity-100 transition-opacity" />
              </NavLink>
            )
          })}
        </nav>
        <div className="mt-7 border-t border-border/70 pt-4 text-xs text-muted-foreground/90">
          <p>{tagline}</p>
        </div>
          <div className="h-4 md:h-5" />
      </div>
    </div>
  )
}
